/**
 * Chef order-calendar model — derives the right-rail month calendar's color
 * codes + deadline countdown from kitchen shipments, keyed on each destination
 * school's order deadline.
 *
 * Mapping (the kitchen page's legend labels):
 *   - green "Orders confirmed" → a service date whose week is CLOSED for every
 *                                destination school (orders finalized).
 *   - black "Orders incoming"  → a future date in an OPEN week (the school can
 *                                still change the order; numbers are a preview).
 *
 * A kitchen can serve several schools: a week is "open" if ANY destination
 * school's window is still open, and the countdown shows the soonest deadline.
 * Weeks with no shipments are left uncolored. Pure — unit-tested directly.
 */

import { shipmentDayKey } from '@ordo/constants/shipment-date-axis'

import type { KitchenServedSchoolDeadline, KitchenShipmentSummary } from '@/api/kitchen-orders'
import { mondayOf } from '@/lib/school-orders/calendar'
import { deadlineStatus, type SchoolDeadlinePolicy } from '@/lib/school-orders/deadline'

export interface ChefOrderCalendar {
  /** Service dates (`YYYY-MM-DD`) whose week is finalized → green "Orders confirmed". */
  orderedDates: ReadonlySet<string>
  /** True when every destination school's window for that week has closed. */
  isWeekClosed(mondayKey: string): boolean
  /** Soonest-closing (open-preferred) destination-school policy for the week's countdown, or null. */
  representativePolicy(mondayKey: string): SchoolDeadlinePolicy | null
}

function toPolicy(d: KitchenShipmentSummary['destinationSchoolDeadline']): SchoolDeadlinePolicy {
  return {
    orderDeadlineDayOfWeek: d.orderDeadlineDayOfWeek as SchoolDeadlinePolicy['orderDeadlineDayOfWeek'],
    orderDeadlineWeeksBefore: d.orderDeadlineWeeksBefore,
    orderDeadlineTime: d.orderDeadlineTime,
    timeZone: d.timeZone,
  }
}

export function buildChefOrderCalendar(
  shipments: ReadonlyArray<KitchenShipmentSummary>,
  now: Date = new Date(),
  servedSchools: ReadonlyArray<KitchenServedSchoolDeadline> = [],
): ChefOrderCalendar {
  // The set of destination schools this kitchen serves. Their deadline
  // policies apply to EVERY week — including future weeks with no orders yet —
  // so the calendar can show "order window open" for weeks the kitchen can
  // still receive orders for, not just the ordered ones. Seeded from the
  // menu-assignment-derived `servedSchools` (so a cold-start kitchen with zero
  // shipments still renders open weeks), then deduped across ALL shipments
  // (covers kitchens routed only via per-component vendor overrides).
  const kitchenPolicies = new Map<string, SchoolDeadlinePolicy>()
  // Per week (Monday) → DELIVERY dates that carry shipments (for "Orders
  // confirmed"). Delivery axis, matching the calendar's own bucketing: a week
  // is marked as carrying orders when the kitchen has to SHIP that week.
  const datesByWeek = new Map<string, Set<string>>()

  for (const school of servedSchools) {
    kitchenPolicies.set(school.schoolLocationId, toPolicy(school.deadline))
  }

  for (const s of shipments) {
    const dateKey = shipmentDayKey(s, 'DELIVERY')
    const monday = mondayOf(dateKey)
    if (!kitchenPolicies.has(s.schoolLocationId)) {
      kitchenPolicies.set(s.schoolLocationId, toPolicy(s.destinationSchoolDeadline))
    }
    let dates = datesByWeek.get(monday)
    if (!dates) {
      dates = new Set()
      datesByWeek.set(monday, dates)
    }
    dates.add(dateKey)
  }

  // A week is "open" if ANY school this kitchen serves still has its window open
  // for that week — evaluated against the school's deadline policy, so it works
  // for weeks that don't yet carry shipments.
  const weekOpen = (monday: string): boolean => {
    for (const policy of kitchenPolicies.values()) {
      if (!deadlineStatus(monday, policy, now).closed) {
        return true
      }
    }
    return false
  }

  const isWeekClosed = (monday: string): boolean => !weekOpen(monday)

  const orderedDates = new Set<string>()
  for (const [monday, dates] of datesByWeek) {
    if (isWeekClosed(monday)) {
      for (const d of dates) {
        orderedDates.add(d)
      }
    }
  }

  const representativePolicy = (monday: string): SchoolDeadlinePolicy | null => {
    if (kitchenPolicies.size === 0) {
      return null
    }
    const ranked = [...kitchenPolicies.values()]
      .map((p) => ({ p, s: deadlineStatus(monday, p, now) }))
      .sort((a, b) => {
        // Open schools first, then the soonest deadline among them.
        if (a.s.closed !== b.s.closed) {
          return a.s.closed ? 1 : -1
        }
        const ad = a.s.daysRemaining ?? Number.POSITIVE_INFINITY
        const bd = b.s.daysRemaining ?? Number.POSITIVE_INFINITY
        return ad - bd
      })
    return ranked[0]?.p ?? null
  }

  return { orderedDates, isWeekClosed, representativePolicy }
}
